import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import config from '../config';
import i18n from '../i18n';

const tr = i18n.t.bind(i18n);

// BIT flags φτάνουν ως {type:'Buffer',data:[0|1]} — ίδιος έλεγχος με το App.js
const bitOn = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'object' && Array.isArray(v.data)) return v.data[0] === 1;
  return v === true || v === 1 || v === '1';
};

const LIVE_SEC = 120;      // πόσο μένει ορατή μια κλήση
const POLL_MS = 5000;      // ρυθμός ελέγχου όταν υπάρχει ρυθμισμένο τηλέφωνο
const IDLE_MS = 60000;     // ρυθμός όταν η εταιρεία δεν έχει ρυθμίσει τηλέφωνο
const AUTH_MS = 300000;    // μετά από 401/403: αραιά, ΠΟΤΕ οριστικό σταμάτημα

// Κλήσεις που έκλεισε ο χρήστης, ανά καρτέλα: ούτε το poll των 5" ούτε ένα refresh
// μέσα στα 120" τις ξαναφέρνει. Νέα κλήση = νέο id, άρα εμφανίζεται κανονικά.
const DISMISS_KEY = 'estiasync.call.dismissed';
const readDismissed = () => {
  try {
    const raw = JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]');
    return new Set(Array.isArray(raw) ? raw : []);
  } catch { return new Set(); }
};
const writeDismissed = (set) => {
  try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...set].slice(-25))); } catch {}
};

// Ανοιχτό modal ή ημερολόγιο; Τότε το Escape ανήκει σ' εκείνο, όχι σ' εμάς.
const somethingElseOwnsEscape = () => {
  const b = document.body;
  return b.classList.contains('ReactModal__Body--open')
    || b.classList.contains('no-scroll')
    || b.style.overflow === 'hidden'
    || !!document.querySelector('.ReactModal__Overlay, .modal-overlay, .flatpickr-calendar.open');
};

// Θέση: κάτω δεξιά. Πάνω από 1500px το μενού είναι στην κορυφή και η σελίδα έχει περιθώριο,
// κάτω από 1500px το μενού είναι στο κάτω μέρος (70px) — γι' αυτό ανεβαίνουμε στα 84px.
// ΠΟΤΕ στην κορυφή: εκεί κάθεται η κολλημένη γραμμή εργαλείων των κρατήσεων, που σε tablet
// δεν κυλάει, οπότε το μπάνερ έκανε αναζήτηση και φίλτρα απρόσιτα.
const BANNER_CSS = `
.call-banner {
  position: fixed; right: 16px; bottom: 20px; z-index: 900;
  max-width: min(94vw, 560px);
}
@media (max-width: 1500px) { .call-banner { bottom: 84px; right: 12px; } }
.call-banner-btn { transition: transform .1s ease, filter .1s ease, background .1s ease; touch-action: manipulation; }
.call-banner-btn:active { transform: scale(.94); }
.call-banner-btn:focus-visible { outline: 3px solid #fbbf24; outline-offset: 2px; }
.call-banner-x:hover { background: rgba(255,255,255,.30); }
.call-banner-cta:hover { filter: brightness(1.08); }
@media (prefers-reduced-motion: reduce) {
  .call-banner-btn { transition: none; }
  .call-banner-btn:active { transform: none; }
}
`;

/**
 * Ζωντανή ένδειξη «Καλεί τώρα» για όσους παίρνουν κρατήσεις.
 * Δεν αλλάζει τίποτα μόνη της: ο χρήστης πατά «Νέα κράτηση» για να τραβήξει τον πελάτη.
 * Κλείνει με το × ή με Escape, και δεν ξαναεμφανίζεται για την ίδια κλήση.
 */
export default function IncomingCallBanner({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [call, setCall] = useState(null);
  const [dismissed, setDismissed] = useState(readDismissed);
  const timer = useRef(null);
  const stopped = useRef(false);
  const busy = useRef(false);

  const allowed = user && (bitOn(user.view_admin) || bitOn(user.view_reservation_form) || bitOn(user.view_clients));

  useEffect(() => {
    if (!allowed) return undefined;
    stopped.current = false;
    const tick = async () => {
      if (stopped.current || busy.current) return;
      busy.current = true;
      let delay = POLL_MS;
      if (document.visibilityState === 'visible') {
        try {
          const { data } = await axios.get(`${config.API_BASE_URL}/phone/last-call`);
          delay = data?.configured ? POLL_MS : IDLE_MS;
          const c = data?.call;
          setCall(c && c.ageSec <= LIVE_SEC && c.event !== 'outgoing' ? c : null);
        } catch (e) {
          // Ποτέ μόνιμο σταμάτημα: ένα 401/403 πάγωνε την ένδειξη ως το επόμενο reload.
          delay = (e?.response?.status === 401 || e?.response?.status === 403) ? AUTH_MS : IDLE_MS;
        }
      }
      busy.current = false;
      if (stopped.current) return;
      clearTimeout(timer.current);
      timer.current = setTimeout(tick, delay);
    };
    tick();
    // Οι χρονομετρητές κρυφών καρτελών παγώνουν: με την επιστροφή ζητάμε αμέσως φρέσκια κατάσταση.
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped.current = true;
      clearTimeout(timer.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [allowed]);

  // ΠΡΟΣΟΧΗ: όλα τα hooks μένουν ΠΑΝΩ από το early return.
  const callId = call ? call.id : null;

  const dismiss = useCallback((id) => {
    if (id === null || id === undefined) return;
    setDismissed((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      writeDismissed(next);
      return next;
    });
  }, []);

  const hiddenRoute = location.pathname.startsWith('/login')
    || location.pathname.startsWith('/buzz')
    || location.pathname.startsWith('/solo')
    || location.pathname.startsWith('/admin');
  const visible = !!allowed && !!call && !dismissed.has(callId) && !hiddenRoute;

  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (somethingElseOwnsEscape()) return;   // το Escape ανήκει στο ανοιχτό παράθυρο
      dismiss(callId);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, callId, dismiss]);

  if (!visible) return null;

  const c = call.client;
  const status = call.event === 'incoming' ? tr('Calling now') : call.event === 'established' ? tr('On the line') : tr('Last call');
  const who = c
    ? `${c.lastname || ''} ${c.firstname || ''}`.trim()
    : `${call.number} · ${tr('new client')}`;
  const details = c
    ? [`★${c.rate}`, `${c.reservations || 0} ${tr('visits')}`, c.no_show ? `${c.no_show} no-show` : null, c.comment].filter(Boolean).join(' · ')
    : null;

  return (
    <div
      className="call-banner"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={`${status}: ${who}`}
      style={{
        background: '#111827', color: '#fff', borderRadius: 14, padding: '8px 8px 8px 14px',
        display: 'flex', gap: 10, alignItems: 'center', boxShadow: '0 8px 24px rgba(0,0,0,.40)',
        fontSize: 14,
      }}
    >
      <style>{BANNER_CSS}</style>

      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#fbbf24" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: '0 0 auto' }}>
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"></path>
      </svg>

      <div style={{ minWidth: 0, flex: '1 1 auto' }}>
        <div style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: '#fbbf24', fontWeight: 700 }}>{status}</div>
        <div title={who} style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{who}</div>
        {details && <div title={details} style={{ fontSize: 12.5, color: '#d1d5db', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{details}</div>}
      </div>

      {(bitOn(user.view_admin) || bitOn(user.view_reservation_form)) && (
        <button
          type="button"
          className="call-banner-btn call-banner-cta"
          aria-label={`${tr('New Reservation')} — ${who}`}
          title={tr('New Reservation')}
          onClick={() => { dismiss(callId); navigate(`/reservation-form?call=${callId}`); }}
          style={{
            flex: '0 0 auto', background: '#d97706', color: '#111827', border: 'none', borderRadius: 10,
            minHeight: 44, padding: '0 14px', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {tr('New Reservation')}
        </button>
      )}

      {/* Διαχωριστικό: το «×» να μην πέφτει κολλητά στο πορτοκαλί κουμπί. */}
      <span aria-hidden="true" style={{ flex: '0 0 auto', width: 1, alignSelf: 'stretch', minHeight: 30, background: 'rgba(255,255,255,.20)' }} />

      <button
        type="button"
        className="call-banner-btn call-banner-x"
        onClick={() => dismiss(callId)}
        aria-label={tr('Close')}
        title={`${tr('Close')} (Esc)`}
        style={{
          flex: '0 0 auto', width: 44, height: 44, padding: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(255,255,255,.16)', color: '#fff',
          border: '1px solid rgba(255,255,255,.38)', borderRadius: 12,
          fontSize: 26, lineHeight: 1, cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
        }}
      >
        <span aria-hidden="true" style={{ marginTop: -3 }}>×</span>
      </button>
    </div>
  );
}
